import { DrawCustomMode } from '@mapbox/mapbox-gl-draw';
import { events } from '@mapbox/mapbox-gl-draw/src/constants';
import { Feature, LineString, Position } from 'geojson';
import { nearestPointOnLine, pointToLineDistance, lineString, point } from '@turf/turf';
import { v4 as uuid } from 'uuid';
import { MoteWaypoint } from '@motes-sdk';
import { isFeatureLineString } from './mapbox-draw-utils';

/**
 * Given a route segment, possibly containg a collection of waypoints, and a distinct position; find the index of the preceding waypoint (if any) for the position.
 * @param position the position
 * @param routeSegment a route segment. The segment may contain a collection of waypoints ordered by their position along the route (the first waypoint along the route is the first waypoint in the collection) 
 * @returns the index of the preceding waypoint in the provided array of waypoints, otherwise `-1`
 */
function findPrecedingWaypoint(position: Position, routeSegment: Feature<LineString, { waypoints?: MoteWaypoint[]; }>): number {
    const waypoints = routeSegment.properties.waypoints;
    // If no waypoints currently exist, a preceding waypoint can't be found
    if(!waypoints || waypoints.length < 1) return -1;

    // Declare variable for keeping track of the preceding waypoint while iterating over subsegments of the route segment
    let precedingWaypointIndex = -1;

    // Snap the input position to the route segment
    const positionSnappedToRoute = nearestPointOnLine(routeSegment, position);

    // Iterate over all subsegments of the route segment and check:
    // 1) whether any waypoints have been passed and
    // 2) if the snapped position is contained within the subsegment
    const routeVertices = routeSegment.geometry.coordinates;

    for(let i = 1; i < routeVertices.length; i++) {
        // Create the subsegment
        const subsegment = lineString([routeVertices[i - 1], routeVertices[i]]);

        // Check if a waypoint has been passed
        // If so, increment `preceding WaypointIndex`
        const upcomingWaypointLocation = waypoints[precedingWaypointIndex + 1]?.location;
        if(routeVertices[i - 1][0] == upcomingWaypointLocation[0] && routeVertices[i - 1][1] == upcomingWaypointLocation[1]) precedingWaypointIndex++;

        // Check if the input position is contained within this subsegment (an imprecision of 2 meters is allowed)
        // OR if the last waypoint has been passed
        // ! This method is not 100% theoretically accurate, but it has not failed in practice. If it does, consider ordering all subsegments by their distance to `position` in order to find the best match.
        const distanceToSubsegment = pointToLineDistance(positionSnappedToRoute, subsegment, { units: 'meters' });
        if(distanceToSubsegment < 2 || precedingWaypointIndex == waypoints.length - 1) return precedingWaypointIndex;
    }

    return precedingWaypointIndex;
}

/**
 * Custom Mapbox Draw mode for adding waypoints on routes.
 * See [documentation](https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/MODES.md) for additional information on custom draw modes.
 */
export const WaypointMode: DrawCustomMode = {
    /**
     * Triggered when a mode is being transitioned into.
     * @param options this object is passed via `draw.changeMode('mode', options)`
     * @returns a mutable state object which will be passed to all other lifecycle functions
     */
    onSetup: function(options): {} {
        // Disable panning of the map upon drag movement
        // @ts-ignore
        this.map.dragPan.disable();

        // Get the route segment from which the waypoint originates
        const routeSegment = this.getFeature(options.routeSegmentId).toGeoJSON();

        // Ensure the returned GeoJSON is a `Feature<LineString>`
        if(!isFeatureLineString(routeSegment)) {
            // @ts-ignore
            this.changeMode('select');
            return null;
        }

        // Get all the route segment's associated waypoints
        const waypoints = routeSegment.properties.waypoints || [];
        // Find the index of the preceding waypoint
        const precedingWaypointIndex = findPrecedingWaypoint([options.position.lng, options.position.lat], routeSegment);

        // Use this index to find the start and end point for the new subsegment
        const reroutingStart: Position = waypoints[precedingWaypointIndex]?.location || routeSegment.geometry.coordinates[0];
        const reroutingEnd: Position = waypoints[precedingWaypointIndex + 1]?.location || routeSegment.geometry.coordinates[routeSegment.geometry.coordinates.length - 1];

        // Return state object for use in other lifecycle funtions
        return { position: options.position, routeSegmentId: options.routeSegmentId, storyKey: options.storyKey, waypoints, precedingWaypointIndex, reroutingStart, reroutingEnd };
    },

    /**
     * Triggered when a drag event is detected on the map.
     * @param state a mutable state object created by `onSetup`
     * @param event the captured event that is triggering this lifecycle event
    */
    onDrag: function(state, event) {
        // Update the position in the mutable state object (which is passed to all other lifecycle funtions as well)
        state.position = event.lngLat;
        // Set the `hasMoved` flag to `true` now that the user has initiated a dragging movement
        if(!state.hasMoved) state.hasMoved = true;
        // Force rendering
        this.doRender(state.routeSegmentId);
    },

    /**
     * Triggered when the mouse is moved.
     * @param state a mutible state object created by `onSetup`
     * @param event the captured event that is triggering this lifecycle event
    */
    onMouseMove: function(state, event): void {
        // If the user simply clicks a route segment without dragging, `WaypointMode` is activated in an unwanted state (`onDrag` and `onMouseUp` are never invoked)
        // In this case, change back to `SelectMode` as soon as the mouse moves
        // ? A (possibly) more optimal solution is to activate `WayPointMode` upon `onDrag` in `SelectMode`, but this is a more extensive refactoring
        // @ts-ignore
        this.changeMode('select');
    },

    /**
     * Triggered when the mouse button is released.
     * @param state a mutable state object created by `onSetup`
     * @param event the captured event that is triggering this lifecycle event
    */
    onMouseUp: function(state, event) {
        // Create a waypoint `Feature<Point>`
        const waypointPosition: [number, number] = [event.lngLat.lng, event.lngLat.lat];
        const waypointProperties = { meta: 'waypoint', storyKey: state.storyKey, isSnapped: true };
        const waypointOptions = { id: uuid() };
        const waypointFeature = point(waypointPosition, waypointProperties, waypointOptions);

        // Convert it to a `DrawFeature` and add it to Draw's data store
        const waypointDrawFeature = this.newFeature(waypointFeature);
        this.addFeature(waypointDrawFeature);

        // Add the waypoint to the array of existing story (`Mote`) waypoints
        const waypoint: MoteWaypoint = { id: waypointOptions.id, location: waypointPosition, isSnapped: true };
        const allWaypoints: MoteWaypoint[] = state.waypoints || [];
        allWaypoints.splice(state.precedingWaypointIndex + 1, 0, waypoint);

        // @ts-ignore
        // Fire `draw.create` event containing the waypoint feature and the array containing all waypoints for the story (`Mote`)
        this.map.fire(events.CREATE, {
            features: [waypointFeature],
            waypoints: allWaypoints,
        });

        // Enable panning of the map upon drag movement
        // @ts-ignore
        this.map.dragPan.enable();
        // Change back to select mode
        // @ts-ignore
        this.changeMode('select');
    },

    /**
     * `toDisplayFeatures` is the only required function for a mode.
     * It decides which features currently in Draw's data store will be rendered on the map.
     * All features passed to `display` will be rendered, so you can pass multiple display features per internal feature.
     * See the *Styling Draw* section in the API documentation for advice on making display features: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#styling-draw
     * @param state a mutable state object created by `onSetup`
     * @param geojson a GeoJSON being evaulated. To render, pass to display
     * @param display all GeoJSON objects passed to this will be rendered onto the map
     */
    toDisplayFeatures: function(state, geojson, display) {
        // If the input GeoJSON is the route segment from which the waypoint originates, render the waypoint and a rerouting indicator (a LineString indicating start and end point of the new subsegment of the route segment)
        if(geojson.type == 'Feature' && geojson.properties.id == state.routeSegmentId) {
            // Ensure that the user has begun dragging the waypoint before rendering
            if(state.hasMoved) {
                // Create and display waypoint
                const waypoint = point([state.position.lng, state.position.lat], { meta: 'waypoint' });
                display(waypoint);

                // Create and display rerouting indicator
                const reroutingIndicator = lineString([state.reroutingStart, waypoint.geometry.coordinates, state.reroutingEnd], { meta: 'reroutingIndicator' });
                display(reroutingIndicator);
            }
        }

        // Render input geojson
        display(geojson);
    }
};
